import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import type { CameraMarker, Fixture, FixtureKind, Planogram, Wall } from "../types";
import {
  DEG,
  KIND_COLOR,
  KINDS,
  PLAN_MARGIN as MARGIN,
  cameraBlockers,
  conePath,
  fixtureBlocksView,
  hasRack,
  viewPath,
} from "../lib/planogram";
import { ui } from "../lib/ui";
import { Select } from "./Select";

/** Interactive floor-plan editor.
 *
 *  The operator draws the store outline (metres), drags out fixtures (shelves,
 *  counters, fridges, zones) and places camera markers with an aim and field
 *  of view. Everything is vector shapes — nothing is uploaded and no imagery
 *  is involved; only the geometry is saved. The camera cones over the plan are
 *  the spatial grounding a multi-camera heatmap needs.
 *
 *  Coordinates are metres, origin top-left, y growing downwards. Camera
 *  rotation is degrees, 0 = towards +x, clockwise — which is exactly SVG's
 *  angle convention, so no conversion anywhere. */

type Point = [number, number];

interface PlanogramEditorProps {
  value: Planogram;
  onChange: (plan: Planogram) => void;
  /** Open the processing setup for one camera marker ("Set up" in its
   *  properties row) — provided by PlanogramPanel. */
  onSetup?: (cameraId: string) => void;
  /** Open the shelf designer for one fixture ("Design shelves…" in its
   *  properties row, or double-click on the plan) — provided by PlanogramPanel. */
  onDesign?: (fixtureId: string) => void;
  /** Fixture to start with selected (coming back from its shelf designer). */
  initialFixtureId?: string | null;
}

type Tool = "select" | "fixture" | "camera" | "wall";

type Selection = { type: "fixture" | "camera" | "wall"; id: string } | null;

type Drag =
  | { mode: "draw"; origin: Point; current: Point }
  | { mode: "wall-draw"; origin: Point; current: Point }
  | { mode: "move"; type: "fixture" | "camera" | "wall"; id: string; offset: Point }
  | { mode: "resize"; id: string }
  | { mode: "wall-end"; id: string; end: "1" | "2" }
  | { mode: "aim"; id: string };

const MIN_FIXTURE = 0.3; // metres — a drag smaller than this is a mis-click
const HANDLE_HIT_PX = 12; // screen pixels within which a click grabs a handle
const CAMERA_HIT_PX = 12;
const WALL_HIT_PX = 8; // screen pixels within which a click selects a wall

const LABEL = "pointer-events-none select-none fill-text font-sans";

const snap = (v: number) => Math.round(v * 10) / 10;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/** First free `<prefix><n>` id. */
function nextId(prefix: string, taken: { id: string }[]): string {
  const ids = new Set(taken.map((t) => t.id));
  let n = taken.length + 1;
  while (ids.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

export function PlanogramEditor({
  value,
  onChange,
  onSetup,
  onDesign,
  initialFixtureId,
}: PlanogramEditorProps) {
  const [tool, setTool] = useState<Tool>("select");
  const [fixtureKind, setFixtureKind] = useState<FixtureKind>("shelf");
  const [selection, setSelection] = useState<Selection>(() =>
    initialFixtureId && value.fixtures.some((f) => f.id === initialFixtureId)
      ? { type: "fixture", id: initialFixtureId }
      : null,
  );
  const [drag, setDrag] = useState<Drag | null>(null);
  // Metres per screen pixel — keeps handles and text a constant on-screen size.
  const [k, setK] = useState(0.02);

  const svgRef = useRef<SVGSVGElement>(null);

  const { width, height, fixtures, walls, cameras } = value;
  const selectedFixture =
    selection?.type === "fixture" ? fixtures.find((f) => f.id === selection.id) : undefined;
  const selectedCamera =
    selection?.type === "camera" ? cameras.find((c) => c.id === selection.id) : undefined;
  const selectedWall =
    selection?.type === "wall" ? walls.find((w) => w.id === selection.id) : undefined;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const update = () => {
      const w = svg.getBoundingClientRect().width;
      if (w > 0) setK((width + 2 * MARGIN) / w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(svg);
    return () => ro.disconnect();
  }, [width]);

  // What each camera actually sees — its cone clipped by walls and blocking
  // fixtures — recomputed live while dragging so cuts follow the geometry.
  const views = useMemo(
    () =>
      new Map(
        cameras.map((c) => [c.id, viewPath(c, cameraBlockers(c, walls, fixtures))]),
      ),
    [cameras, walls, fixtures],
  );

  // --- model helpers ---------------------------------------------------------

  const patchFixture = (id: string, patch: Partial<Fixture>) =>
    onChange({
      ...value,
      fixtures: fixtures.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });

  const patchCamera = (id: string, patch: Partial<CameraMarker>) =>
    onChange({
      ...value,
      cameras: cameras.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });

  const patchWall = (id: string, patch: Partial<Wall>) =>
    onChange({
      ...value,
      walls: walls.map((w) => (w.id === id ? { ...w, ...patch } : w)),
    });

  const removeSelected = useCallback(() => {
    if (!selection) return;
    if (selection.type === "fixture") {
      onChange({ ...value, fixtures: fixtures.filter((f) => f.id !== selection.id) });
    } else if (selection.type === "wall") {
      onChange({ ...value, walls: walls.filter((w) => w.id !== selection.id) });
    } else {
      onChange({ ...value, cameras: cameras.filter((c) => c.id !== selection.id) });
    }
    setSelection(null);
  }, [selection, value, fixtures, walls, cameras, onChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && selection) {
        e.preventDefault();
        removeSelected();
      }
      if (e.key === "Escape") {
        setSelection(null);
        setTool("select");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selection, removeSelected]);

  // --- coordinate conversion -------------------------------------------------

  /** Screen → plan metres, snapped to 0.1 m and clamped to the outline. */
  const toPlan = useCallback(
    (clientX: number, clientY: number): Point | null => {
      const svg = svgRef.current;
      if (!svg) return null;
      const rect = svg.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      const vw = width + 2 * MARGIN;
      const vh = height + 2 * MARGIN;
      const x = ((clientX - rect.left) / rect.width) * vw - MARGIN;
      const y = ((clientY - rect.top) / rect.height) * vh - MARGIN;
      return [clamp(snap(x), 0, width), clamp(snap(y), 0, height)];
    },
    [width, height],
  );

  /** Plan metres → client (screen) pixels. */
  const toScreen = (p: Point): Point | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const vw = width + 2 * MARGIN;
    const vh = height + 2 * MARGIN;
    return [
      rect.left + ((p[0] + MARGIN) / vw) * rect.width,
      rect.top + ((p[1] + MARGIN) / vh) * rect.height,
    ];
  };

  /** Screen-pixel distance between a client position and a plan point. */
  const screenDistance = (clientX: number, clientY: number, p: Point) => {
    const s = toScreen(p);
    return s ? Math.hypot(clientX - s[0], clientY - s[1]) : Infinity;
  };

  /** Screen-pixel distance between a client position and a wall segment. */
  const wallScreenDistance = (clientX: number, clientY: number, w: Wall) => {
    const a = toScreen([w.x1, w.y1]);
    const b = toScreen([w.x2, w.y2]);
    if (!a || !b) return Infinity;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len2 = dx * dx + dy * dy;
    const t =
      len2 === 0
        ? 0
        : clamp(((clientX - a[0]) * dx + (clientY - a[1]) * dy) / len2, 0, 1);
    return Math.hypot(clientX - (a[0] + t * dx), clientY - (a[1] + t * dy));
  };

  const aimHandle = (cam: CameraMarker): Point => [
    cam.x + Math.cos(cam.rotation * DEG) * cam.range,
    cam.y + Math.sin(cam.rotation * DEG) * cam.range,
  ];

  // --- pointer interaction ---------------------------------------------------

  const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const p = toPlan(e.clientX, e.clientY);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);

    if (tool === "fixture") {
      setDrag({ mode: "draw", origin: p, current: p });
      return;
    }
    if (tool === "wall") {
      setDrag({ mode: "wall-draw", origin: p, current: p });
      return;
    }
    if (tool === "camera") {
      const id = nextId("cam", cameras);
      onChange({
        ...value,
        cameras: [
          ...cameras,
          { id, label: "", x: p[0], y: p[1], rotation: 0, fov: 90, range: 5, entry: false },
        ],
      });
      setSelection({ type: "camera", id });
      setTool("select");
      return;
    }

    // Select tool. Handles of the current selection take priority, then
    // cameras (small targets), then the topmost fixture under the pointer.
    if (
      selectedCamera &&
      screenDistance(e.clientX, e.clientY, aimHandle(selectedCamera)) <= HANDLE_HIT_PX
    ) {
      setDrag({ mode: "aim", id: selectedCamera.id });
      return;
    }
    if (
      selectedFixture &&
      screenDistance(e.clientX, e.clientY, [
        selectedFixture.x + selectedFixture.w,
        selectedFixture.y + selectedFixture.h,
      ]) <= HANDLE_HIT_PX
    ) {
      setDrag({ mode: "resize", id: selectedFixture.id });
      return;
    }
    if (selectedWall) {
      for (const end of ["1", "2"] as const) {
        const pt: Point =
          end === "1"
            ? [selectedWall.x1, selectedWall.y1]
            : [selectedWall.x2, selectedWall.y2];
        if (screenDistance(e.clientX, e.clientY, pt) <= HANDLE_HIT_PX) {
          setDrag({ mode: "wall-end", id: selectedWall.id, end });
          return;
        }
      }
    }
    const cam = [...cameras]
      .reverse()
      .find((c) => screenDistance(e.clientX, e.clientY, [c.x, c.y]) <= CAMERA_HIT_PX);
    if (cam) {
      setSelection({ type: "camera", id: cam.id });
      setDrag({ mode: "move", type: "camera", id: cam.id, offset: [p[0] - cam.x, p[1] - cam.y] });
      return;
    }
    const wall = [...walls]
      .reverse()
      .find((w) => wallScreenDistance(e.clientX, e.clientY, w) <= WALL_HIT_PX);
    if (wall) {
      setSelection({ type: "wall", id: wall.id });
      setDrag({
        mode: "move",
        type: "wall",
        id: wall.id,
        offset: [p[0] - wall.x1, p[1] - wall.y1],
      });
      return;
    }
    const fixture = [...fixtures]
      .reverse()
      .find((f) => p[0] >= f.x && p[0] <= f.x + f.w && p[1] >= f.y && p[1] <= f.y + f.h);
    if (fixture) {
      setSelection({ type: "fixture", id: fixture.id });
      setDrag({
        mode: "move",
        type: "fixture",
        id: fixture.id,
        offset: [p[0] - fixture.x, p[1] - fixture.y],
      });
      return;
    }
    setSelection(null);
  };

  const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    const p = toPlan(e.clientX, e.clientY);
    if (!p) return;

    if (drag.mode === "draw" || drag.mode === "wall-draw") {
      setDrag({ ...drag, current: p });
    } else if (drag.mode === "wall-end") {
      const w = walls.find((w) => w.id === drag.id);
      if (w) {
        const other: Point = drag.end === "1" ? [w.x2, w.y2] : [w.x1, w.y1];
        if (p[0] !== other[0] || p[1] !== other[1]) {
          patchWall(
            drag.id,
            drag.end === "1" ? { x1: p[0], y1: p[1] } : { x2: p[0], y2: p[1] },
          );
        }
      }
    } else if (drag.mode === "move" && drag.type === "wall") {
      const w = walls.find((w) => w.id === drag.id);
      if (w) {
        let dx = snap(p[0] - drag.offset[0]) - w.x1;
        let dy = snap(p[1] - drag.offset[1]) - w.y1;
        dx = clamp(dx, -Math.min(w.x1, w.x2), width - Math.max(w.x1, w.x2));
        dy = clamp(dy, -Math.min(w.y1, w.y2), height - Math.max(w.y1, w.y2));
        patchWall(drag.id, {
          x1: snap(w.x1 + dx),
          y1: snap(w.y1 + dy),
          x2: snap(w.x2 + dx),
          y2: snap(w.y2 + dy),
        });
      }
    } else if (drag.mode === "move" && drag.type === "fixture") {
      const f = fixtures.find((f) => f.id === drag.id);
      if (f) {
        patchFixture(drag.id, {
          x: clamp(snap(p[0] - drag.offset[0]), 0, Math.max(0, width - f.w)),
          y: clamp(snap(p[1] - drag.offset[1]), 0, Math.max(0, height - f.h)),
        });
      }
    } else if (drag.mode === "move") {
      patchCamera(drag.id, {
        x: clamp(snap(p[0] - drag.offset[0]), 0, width),
        y: clamp(snap(p[1] - drag.offset[1]), 0, height),
      });
    } else if (drag.mode === "resize") {
      const f = fixtures.find((f) => f.id === drag.id);
      if (f) {
        patchFixture(drag.id, {
          w: Math.max(MIN_FIXTURE, snap(p[0] - f.x)),
          h: Math.max(MIN_FIXTURE, snap(p[1] - f.y)),
        });
      }
    } else {
      const c = cameras.find((c) => c.id === drag.id);
      if (c) {
        const dist = Math.hypot(p[0] - c.x, p[1] - c.y);
        const angle = Math.atan2(p[1] - c.y, p[0] - c.x) / DEG;
        patchCamera(drag.id, {
          rotation: Math.round(angle / 5) * 5,
          range: clamp(snap(dist), 0.5, 100),
        });
      }
    }
  };

  /** Double-click a fixture that has a rack to design its shelves. */
  const onDoubleClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (!onDesign || tool !== "select") return;
    const p = toPlan(e.clientX, e.clientY);
    if (!p) return;
    const fixture = [...fixtures]
      .reverse()
      .find((f) => p[0] >= f.x && p[0] <= f.x + f.w && p[1] >= f.y && p[1] <= f.y + f.h);
    if (fixture && hasRack(fixture)) onDesign(fixture.id);
  };

  const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!drag) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (drag.mode === "draw") {
      const x = Math.min(drag.origin[0], drag.current[0]);
      const y = Math.min(drag.origin[1], drag.current[1]);
      // Re-snap: the difference of two snapped coordinates can drift
      // (0.6000000000000001) in float arithmetic.
      const w = snap(Math.abs(drag.current[0] - drag.origin[0]));
      const h = snap(Math.abs(drag.current[1] - drag.origin[1]));
      if (w >= MIN_FIXTURE && h >= MIN_FIXTURE) {
        const id = nextId("fx", fixtures);
        onChange({
          ...value,
          fixtures: [
            ...fixtures,
            {
              id,
              kind: fixtureKind,
              label: "",
              x,
              y,
              w,
              h,
              height: null,
              shelves: [],
              blocks_view: null,
            },
          ],
        });
        setSelection({ type: "fixture", id });
        setTool("select");
      }
    }
    if (drag.mode === "wall-draw") {
      const [x1, y1] = drag.origin;
      const [x2, y2] = drag.current;
      if (Math.hypot(x2 - x1, y2 - y1) >= MIN_FIXTURE) {
        const id = nextId("w", walls);
        onChange({ ...value, walls: [...walls, { id, x1, y1, x2, y2 }] });
        setSelection({ type: "wall", id });
        setTool("select");
      }
    }
    setDrag(null);
  };

  // --- render ----------------------------------------------------------------

  const draftRect =
    drag?.mode === "draw"
      ? {
          x: Math.min(drag.origin[0], drag.current[0]),
          y: Math.min(drag.origin[1], drag.current[1]),
          w: Math.abs(drag.current[0] - drag.origin[0]),
          h: Math.abs(drag.current[1] - drag.origin[1]),
        }
      : null;
  const draftWall = drag?.mode === "wall-draw" ? drag : null;

  const cursor =
    tool !== "select" ? "cursor-crosshair" : drag ? "cursor-grabbing" : "cursor-default";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={ui.label}>Draw</span>
        <button
          type="button"
          className={`${ui.btnQuiet} ${tool === "select" ? ui.btnSelected : ""}`}
          onClick={() => setTool("select")}
        >
          Select
        </button>
        <button
          type="button"
          className={`${ui.btnQuiet} ${tool === "wall" ? ui.btnSelected : ""}`}
          onClick={() => setTool("wall")}
        >
          ─ Wall
        </button>
        {KINDS.map(({ kind, label, color }) => (
          <button
            key={kind}
            type="button"
            className={`${ui.btnQuiet} ${
              tool === "fixture" && fixtureKind === kind ? ui.btnSelected : ""
            }`}
            onClick={() => {
              setTool("fixture");
              setFixtureKind(kind);
            }}
          >
            <span
              className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[3px] align-[-1px]"
              style={{ background: color }}
            />
            {label}
          </button>
        ))}
        <button
          type="button"
          className={`${ui.btnQuiet} ${tool === "camera" ? ui.btnSelected : ""}`}
          onClick={() => setTool("camera")}
        >
          ◉ Camera
        </button>
        <span className="ml-auto flex items-center gap-2 text-xs text-muted">
          Store
          <input
            className={`${ui.input} w-[72px]`}
            type="number"
            min={1}
            max={200}
            step={0.5}
            value={width}
            onChange={(e) =>
              onChange({ ...value, width: clamp(Number(e.target.value) || 1, 1, 200) })
            }
            aria-label="Store width (m)"
          />
          ×
          <input
            className={`${ui.input} w-[72px]`}
            type="number"
            min={1}
            max={200}
            step={0.5}
            value={height}
            onChange={(e) =>
              onChange({ ...value, height: clamp(Number(e.target.value) || 1, 1, 200) })
            }
            aria-label="Store depth (m)"
          />
          m
        </span>
      </div>

      <div className="max-w-[960px] overflow-hidden rounded-card border border-border bg-surface leading-[0]">
        <svg
          ref={svgRef}
          className={`block h-auto w-full touch-none ${cursor}`}
          viewBox={`${-MARGIN} ${-MARGIN} ${width + 2 * MARGIN} ${height + 2 * MARGIN}`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
        >
          <defs>
            <pattern id="plan-grid" width={1} height={1} patternUnits="userSpaceOnUse">
              <path
                d="M 1 0 L 0 0 0 1"
                fill="none"
                stroke="var(--border)"
                strokeWidth={k}
                opacity={0.7}
              />
            </pattern>
          </defs>

          {/* floor + 1 m grid + walls */}
          <rect x={0} y={0} width={width} height={height} fill="var(--bg)" />
          <rect x={0} y={0} width={width} height={height} fill="url(#plan-grid)" />
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="none"
            stroke="var(--text)"
            strokeWidth={3}
            vectorEffect="non-scaling-stroke"
            opacity={0.75}
          />

          {/* what each camera actually sees (cone clipped by walls and
              blocking fixtures), under everything else; the selected camera
              also shows its full unclipped cone dashed, so where the view is
              cut reads at a glance */}
          {cameras.map((c) => {
            const color = c.entry ? "var(--success)" : "var(--accent)";
            const selected = selection?.type === "camera" && selection.id === c.id;
            return (
              <g key={`cone-${c.id}`}>
                {selected &&
                  (c.fov >= 360 ? (
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={c.range}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.25}
                      strokeDasharray={`${4 * k} ${3 * k}`}
                      vectorEffect="non-scaling-stroke"
                      opacity={0.55}
                    />
                  ) : (
                    <path
                      d={conePath(c)}
                      fill="none"
                      stroke={color}
                      strokeWidth={1.25}
                      strokeDasharray={`${4 * k} ${3 * k}`}
                      vectorEffect="non-scaling-stroke"
                      opacity={0.55}
                    />
                  ))}
                <path d={views.get(c.id) ?? ""} fill={color} opacity={0.14} />
              </g>
            );
          })}

          {fixtures.map((f) => {
            const color = KIND_COLOR[f.kind];
            const selected = selection?.type === "fixture" && selection.id === f.id;
            // Room for a second line under the label ("5 shelves")?
            const shelfNote =
              f.shelves.length > 0 && Math.min(f.w, f.h) >= 30 * k
                ? `${f.shelves.length} ${f.shelves.length === 1 ? "shelf" : "shelves"}`
                : "";
            return (
              <g key={f.id}>
                <rect
                  x={f.x}
                  y={f.y}
                  width={f.w}
                  height={f.h}
                  fill={color}
                  fillOpacity={f.kind === "zone" ? 0.06 : 0.18}
                  stroke={selected ? "var(--accent)" : color}
                  strokeWidth={selected ? 2.5 : 1.5}
                  strokeDasharray={f.kind === "zone" ? `${6 * k} ${4 * k}` : undefined}
                  vectorEffect="non-scaling-stroke"
                />
                <text
                  x={f.x + f.w / 2}
                  y={f.y + f.h / 2 - (shelfNote ? 6 * k : 0)}
                  textAnchor="middle"
                  dominantBaseline="central"
                  className={LABEL}
                  fontSize={11 * k}
                >
                  {f.label || f.kind}
                </text>
                {shelfNote && (
                  <text
                    x={f.x + f.w / 2}
                    y={f.y + f.h / 2 + 7 * k}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className={`${LABEL} fill-muted!`}
                    fontSize={9 * k}
                  >
                    {shelfNote}
                  </text>
                )}
                {selected && (
                  <rect
                    x={f.x + f.w - 5 * k}
                    y={f.y + f.h - 5 * k}
                    width={10 * k}
                    height={10 * k}
                    fill="var(--accent)"
                    stroke="var(--bg)"
                    strokeWidth={1.5}
                    vectorEffect="non-scaling-stroke"
                    className="cursor-nwse-resize"
                  />
                )}
              </g>
            );
          })}

          {walls.map((w) => {
            const selected = selection?.type === "wall" && selection.id === w.id;
            return (
              <g key={w.id}>
                <line
                  x1={w.x1}
                  y1={w.y1}
                  x2={w.x2}
                  y2={w.y2}
                  stroke={selected ? "var(--accent)" : "var(--text)"}
                  strokeWidth={selected ? 5 : 4}
                  strokeLinecap="round"
                  vectorEffect="non-scaling-stroke"
                  opacity={0.85}
                />
                {selected &&
                  (["1", "2"] as const).map((end) => (
                    <circle
                      key={end}
                      cx={end === "1" ? w.x1 : w.x2}
                      cy={end === "1" ? w.y1 : w.y2}
                      r={5 * k}
                      fill="var(--bg)"
                      stroke="var(--accent)"
                      strokeWidth={2}
                      vectorEffect="non-scaling-stroke"
                      className="cursor-grab"
                    />
                  ))}
              </g>
            );
          })}

          {cameras.map((c) => {
            const selected = selection?.type === "camera" && selection.id === c.id;
            const color = c.entry ? "var(--success)" : "var(--accent)";
            const handle = aimHandle(c);
            return (
              <g key={c.id}>
                {selected && (
                  <>
                    <line
                      x1={c.x}
                      y1={c.y}
                      x2={handle[0]}
                      y2={handle[1]}
                      stroke={color}
                      strokeWidth={1.5}
                      strokeDasharray={`${4 * k} ${3 * k}`}
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={handle[0]}
                      cy={handle[1]}
                      r={5 * k}
                      fill="var(--bg)"
                      stroke={color}
                      strokeWidth={2}
                      vectorEffect="non-scaling-stroke"
                      className="cursor-grab"
                    />
                  </>
                )}
                <circle
                  cx={c.x}
                  cy={c.y}
                  r={(selected ? 8 : 6.5) * k}
                  fill={color}
                  stroke="var(--bg)"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  className="cursor-grab"
                />
                <text
                  x={c.x}
                  y={c.y - 12 * k}
                  textAnchor="middle"
                  className={LABEL}
                  fontSize={11 * k}
                  fontWeight={600}
                >
                  {c.label || c.id}
                  {c.entry ? " · entry" : ""}
                </text>
              </g>
            );
          })}

          {draftRect && draftRect.w > 0 && draftRect.h > 0 && (
            <rect
              x={draftRect.x}
              y={draftRect.y}
              width={draftRect.w}
              height={draftRect.h}
              fill={KIND_COLOR[fixtureKind]}
              fillOpacity={0.12}
              stroke={KIND_COLOR[fixtureKind]}
              strokeWidth={1.5}
              strokeDasharray={`${5 * k} ${4 * k}`}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {draftWall && (
            <line
              x1={draftWall.origin[0]}
              y1={draftWall.origin[1]}
              x2={draftWall.current[0]}
              y2={draftWall.current[1]}
              stroke="var(--text)"
              strokeWidth={4}
              strokeLinecap="round"
              strokeDasharray={`${5 * k} ${4 * k}`}
              vectorEffect="non-scaling-stroke"
              opacity={0.6}
            />
          )}
        </svg>
      </div>

      <p className="text-xs text-muted">
        Drag ─ Wall to trace the real store shape (diagonals included) — the
        outline is just the bounding box. Pick a fixture type and drag on the
        plan to draw it; click ◉ Camera then click where a camera hangs. With
        Select, drag things to move them, use the corner square to resize a
        fixture, a wall's end dots to reshape it, and the ring handle to aim a
        camera and set how far it sees. Double-click a shelf, fridge or counter
        to design its shelves and product placement. Delete removes the
        selected item. Camera cones are cut by walls and by fixtures tall
        enough to block the view (shelves and fridges by default, or any rack
        designed 1.4 m or taller) — select a camera to compare its full cone
        (dashed) with what it actually sees, and use a fixture's “Camera view”
        setting to override whether cameras see over it.
      </p>

      {selectedWall && (
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface px-4 py-3">
          {(
            [
              ["x1", "Start x (m)"],
              ["y1", "Start y (m)"],
              ["x2", "End x (m)"],
              ["y2", "End y (m)"],
            ] as const
          ).map(([field, label]) => (
            <label key={field} className={ui.field}>
              {label}
              <input
                className={`${ui.input} w-[84px]`}
                type="number"
                step={0.1}
                value={selectedWall[field]}
                onChange={(e) =>
                  patchWall(selectedWall.id, {
                    [field]: clamp(
                      Number(e.target.value),
                      0,
                      field === "x1" || field === "x2" ? width : height,
                    ),
                  })
                }
              />
            </label>
          ))}
          <button type="button" className={ui.btnDanger} onClick={removeSelected}>
            Remove
          </button>
        </div>
      )}

      {selectedFixture && (
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface px-4 py-3">
          <label className={ui.field}>
            Type
            <Select
              value={selectedFixture.kind}
              onChange={(e) =>
                patchFixture(selectedFixture.id, { kind: e.target.value as FixtureKind })
              }
            >
              {KINDS.map(({ kind, label }) => (
                <option key={kind} value={kind}>
                  {label}
                </option>
              ))}
            </Select>
          </label>
          <label className={ui.field}>
            Label
            <input
              className={`${ui.input} min-w-[160px]`}
              value={selectedFixture.label}
              placeholder={selectedFixture.kind}
              onChange={(e) => patchFixture(selectedFixture.id, { label: e.target.value })}
            />
          </label>
          {(
            [
              ["x", "X (m)"],
              ["y", "Y (m)"],
              ["w", "Width (m)"],
              ["h", "Depth (m)"],
            ] as const
          ).map(([field, label]) => (
            <label key={field} className={ui.field}>
              {label}
              <input
                className={`${ui.input} w-[84px]`}
                type="number"
                step={0.1}
                value={selectedFixture[field]}
                onChange={(e) =>
                  patchFixture(selectedFixture.id, {
                    [field]:
                      field === "w" || field === "h"
                        ? Math.max(MIN_FIXTURE, Number(e.target.value))
                        : Math.max(0, Number(e.target.value)),
                  })
                }
              />
            </label>
          ))}
          <label className={ui.field}>
            Camera view
            <Select
              value={
                selectedFixture.blocks_view === null ||
                selectedFixture.blocks_view === undefined
                  ? "auto"
                  : selectedFixture.blocks_view
                    ? "blocks"
                    : "over"
              }
              onChange={(e) =>
                patchFixture(selectedFixture.id, {
                  blocks_view:
                    e.target.value === "auto" ? null : e.target.value === "blocks",
                })
              }
              title="Whether cameras see past this fixture — cuts their view cones and the heatmap behind it"
            >
              <option value="auto">
                Auto —{" "}
                {fixtureBlocksView({ ...selectedFixture, blocks_view: null })
                  ? "blocks view"
                  : "seen over"}
              </option>
              <option value="blocks">Blocks view</option>
              <option value="over">Cameras see over it</option>
            </Select>
          </label>
          {onDesign && hasRack(selectedFixture) && (
            <button
              type="button"
              className={ui.btn}
              onClick={() => onDesign(selectedFixture.id)}
            >
              Design shelves…
              {selectedFixture.shelves.length > 0 &&
                ` (${selectedFixture.shelves.length})`}
            </button>
          )}
          <button type="button" className={ui.btnDanger} onClick={removeSelected}>
            Remove
          </button>
        </div>
      )}

      {selectedCamera && (
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-surface px-4 py-3">
          <label className={ui.field}>
            Label
            <input
              className={`${ui.input} min-w-[160px]`}
              value={selectedCamera.label}
              placeholder={selectedCamera.id}
              onChange={(e) => patchCamera(selectedCamera.id, { label: e.target.value })}
            />
          </label>
          {(
            [
              ["x", "X (m)", 0.1],
              ["y", "Y (m)", 0.1],
              ["rotation", "Aim (°)", 5],
              ["fov", "FOV (°)", 5],
              ["range", "Range (m)", 0.5],
            ] as const
          ).map(([field, label, step]) => (
            <label key={field} className={ui.field}>
              {label}
              <input
                className={`${ui.input} w-[84px]`}
                type="number"
                step={step}
                value={selectedCamera[field]}
                onChange={(e) =>
                  patchCamera(selectedCamera.id, { [field]: Number(e.target.value) })
                }
              />
            </label>
          ))}
          <label className={`${ui.field} flex-row items-center gap-2 self-center pt-4`}>
            <input
              type="checkbox"
              checked={selectedCamera.entry}
              onChange={(e) =>
                // A store may have several entrances, each with its own
                // entrance camera — this flag is per camera.
                patchCamera(selectedCamera.id, { entry: e.target.checked })
              }
            />
            Entrance camera (feeds the counting pipeline)
          </label>
          {onSetup && (
            <button
              type="button"
              className={ui.btn}
              onClick={() => onSetup(selectedCamera.id)}
            >
              Set up…
            </button>
          )}
          <button type="button" className={ui.btnDanger} onClick={removeSelected}>
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
